// Soundness half of the Argument Critique slide-over: weak claims,
// contradictions, logical gaps. Result rendering ported from the retired
// EvidenceMapPanel footer; now persisted (concept.argument_analysis) with
// structure-based staleness. Findings reference claim TEXTS (LLM output has
// no ids) — "show on map" appears only when an exact text match resolves.
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { toast } from "@/lib/toast";
import { useClaims, useClaimActions } from "@/hooks/useClaims";
import type { StoredArgumentAnalysis } from "@/hooks/useClaims";
import type { ToolStatusResult } from "@/lib/analyzeToolState";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";

interface Props {
  conceptId: string;
  stored: StoredArgumentAnalysis | null;
  status: ToolStatusResult;
  onShowClaim: (claimId: string) => void;
}

export function SoundnessSection({ conceptId, stored, status, onShowClaim }: Props) {
  const { data: claimsData } = useClaims(conceptId);
  const { analyzeArgument } = useClaimActions(conceptId);
  // Backend is require_paid — a downgraded user can still VIEW a cached
  // result (cached-beats-lock), but rerun affordances would only 403.
  const { data: currentUser } = useCurrentUser();
  const isPaid = isPaidPlan(currentUser?.plan);
  const isAnalyzing = status.status === "running" || analyzeArgument.isPending;
  const result = stored?.result ?? null;

  const claimIdByText = new Map(
    (claimsData ?? []).map((c) => [c.text, c.id] as const),
  );

  const run = () =>
    analyzeArgument.mutate(undefined, {
      onError: () => toast.error("Argument critique failed"),
    });

  const ShowOnMap = ({ text }: { text: string }) => {
    const id = claimIdByText.get(text);
    if (!id) return null;
    return (
      <button
        onClick={() => onShowClaim(id)}
        className="ml-1 text-[11px] text-primary underline-offset-2 hover:underline"
      >
        show on map
      </button>
    );
  };

  return (
    <section className="rounded-md border border-border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Soundness
        </h3>
        {result && !isAnalyzing && isPaid && (
          <button
            onClick={run}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="h-3 w-3" /> Rerun
          </button>
        )}
      </div>

      {status.status === "locked" && !result && (
        <p className="py-2 text-xs text-muted-foreground">🔒 {status.lockedReason}</p>
      )}

      {status.status === "ready" && !result && !isAnalyzing && (
        <div className="flex flex-col items-center gap-2 py-3">
          <p className="text-xs text-muted-foreground text-center">
            Stress-test your claim structure for weak claims, contradictions,
            and logical gaps.
          </p>
          <button
            onClick={run}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            data-guide-target="argumentCritiqueButton"
          >
            Critique argument
          </button>
        </div>
      )}

      {isAnalyzing && (
        <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Analyzing argument...
        </div>
      )}

      {status.status === "stale" && result && !isAnalyzing && (
        <div
          role="status"
          aria-live="polite"
          data-testid="soundness-stale-banner"
          className="flex items-center justify-between gap-2 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-200"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <AlertTriangle className="size-3.5 shrink-0" />
            <span className="truncate">Your claims or evidence changed since this ran.</span>
          </div>
          {isPaid && (
            <button
              onClick={run}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium hover:bg-amber-500/15"
            >
              <RefreshCw className="size-3" /> Rerun
            </button>
          )}
        </div>
      )}

      {result && !isAnalyzing && (
        <div className="space-y-2 text-xs">
          {result.overall_assessment && (
            <div className="rounded border p-2">
              <p className="mb-1 font-medium">Overall Assessment</p>
              <p className="text-muted-foreground">{result.overall_assessment}</p>
            </div>
          )}
          {result.weak_claims.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-2 dark:border-amber-800 dark:bg-amber-950/30">
              <p className="mb-1 font-medium">Weak Claims</p>
              {result.weak_claims.map((wc, i) => (
                <div key={i} className="mb-1">
                  <p className="font-medium">
                    {wc.claim_text}
                    <ShowOnMap text={wc.claim_text} />
                  </p>
                  <p className="text-muted-foreground">{wc.reason}</p>
                </div>
              ))}
            </div>
          )}
          {result.contradictions.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-950/30">
              <p className="mb-1 font-medium">Contradictions</p>
              {result.contradictions.map((c, i) => (
                <div key={i} className="mb-1">
                  <p>
                    &quot;{c.claim_a}&quot;
                    <ShowOnMap text={c.claim_a} /> vs &quot;{c.claim_b}&quot;
                    <ShowOnMap text={c.claim_b} />
                  </p>
                  <p className="text-muted-foreground">{c.explanation}</p>
                </div>
              ))}
            </div>
          )}
          {result.logical_gaps.length > 0 && (
            <div className="rounded border border-blue-200 bg-blue-50 p-2 dark:border-blue-800 dark:bg-blue-950/30">
              <p className="mb-1 font-medium">Logical Gaps</p>
              {result.logical_gaps.map((g, i) => (
                <p key={i} className="mb-1 text-muted-foreground">
                  {g.description}
                </p>
              ))}
            </div>
          )}
          {result.weak_claims.length === 0 &&
            result.contradictions.length === 0 &&
            result.logical_gaps.length === 0 &&
            !result.overall_assessment && (
              <p className="text-muted-foreground">No issues found.</p>
            )}
        </div>
      )}
    </section>
  );
}
